import React, { FC, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import { Select, TimeZoneEnums } from '@nhl/nhl-breakaway-ui';

import useSitePreferencesCookie from '~/core/hooks/useSitePreferencesCookie';

import { SelectableTimezone } from './types';
import { getTimezoneSynonym, SYSTEM_ZONE } from './utils';

type Props = {
    defaultTimezone?: string;
    selectedTimezone?: SelectableTimezone;
    onChangeTimezone: (newTimezone: SelectableTimezone) => void;
    showVenueTime?: boolean;
};

const TimezonePicker: FC<Props> = ({ defaultTimezone, selectedTimezone, onChangeTimezone, showVenueTime = true }) => {
    const intl = useIntl();

    const [sitePreferences, setSitePreferences] = useSitePreferencesCookie();
    const [updateSelectedTimezone, setUpdateSelectedTimezone] = useState(false);

    const timezoneOptions: Array<SelectableTimezone> = useMemo(() => {
        const options: Array<SelectableTimezone> = [
            {
                label: intl.formatMessage({
                    description: 'Text which specifies the Newfoundland time offset (ie UTC-4:00)',
                    defaultMessage: 'Newfoundland Time',
                }),
                value: TimeZoneEnums.AMERICA_ST_JOHNS,
                key: TimeZoneEnums.AMERICA_ST_JOHNS,
            },
            {
                label: intl.formatMessage({
                    description: 'Text which specifies the Atlantic named time offset (ie UTC-4:00 or UTC-3:00)',
                    defaultMessage: 'Atlantic Time',
                }),
                value: TimeZoneEnums.AMERICA_HALIFAX,
                key: TimeZoneEnums.AMERICA_HALIFAX,
            },
            {
                label: intl.formatMessage({
                    description: 'Text which specifies the US/Eastern named time offset',
                    defaultMessage: 'Eastern Time',
                }),
                value: TimeZoneEnums.AMERICA_NEW_YORK,
                key: TimeZoneEnums.AMERICA_NEW_YORK,
            },
            {
                label: intl.formatMessage({
                    description: 'Text which specifies the US/Central named time offset (ie UTC-6:00 or UTC-5:00)',
                    defaultMessage: 'Central Time',
                }),
                value: TimeZoneEnums.AMERICA_CHICAGO,
                key: TimeZoneEnums.AMERICA_CHICAGO,
            },
            {
                label: intl.formatMessage({
                    description: 'Text which specifies the US/Mountain Time time offset (ie UTC-7:00 or UTC-6:00)',
                    defaultMessage: 'Mountain Time',
                }),
                value: TimeZoneEnums.AMERICA_DENVER,
                key: TimeZoneEnums.AMERICA_DENVER,
            },
            {
                label: intl.formatMessage({
                    description: 'Text which specifies the US/Arizona time offset (ie UTC-7:00)',
                    defaultMessage: 'Mountain Time (Arizona)',
                }),
                value: TimeZoneEnums.AMERICA_PHOENIX,
                key: TimeZoneEnums.AMERICA_PHOENIX,
            },
            {
                label: intl.formatMessage({
                    description: 'Text which specifies the US/Pacific time offset (ie UTC-8:00 or UTC-7:00)',
                    defaultMessage: 'Pacific Time',
                }),
                value: TimeZoneEnums.AMERICA_LOS_ANGELES,
                key: TimeZoneEnums.AMERICA_LOS_ANGELES,
            },
        ];

        if (showVenueTime) {
            options.push({
                label: intl.formatMessage({
                    description: 'Text which describes the time zone of the venue the game is being played in',
                    defaultMessage: 'Venue Game Time',
                }),
                value: TimeZoneEnums.VENUE,
                key: TimeZoneEnums.VENUE,
            });
        }

        options.push({
            label: intl.formatMessage({
                description: "Text which specifies the user's own timezone",
                defaultMessage: 'Your Time Zone',
            }),
            value: SYSTEM_ZONE,
            key: 'system',
        });

        return options;
    }, [intl, showVenueTime]);

    const onOptionChanged = (e, value: SelectableTimezone) => {
        setSitePreferences({
            ...sitePreferences,
            timezone: value.key,
        });

        onChangeTimezone(value);
    };

    const defaultSelectableTimezone = useMemo(() => {
        let selectableTimezone: SelectableTimezone | undefined;

        if (sitePreferences.timezone) {
            selectableTimezone = timezoneOptions.find((stz) => stz.key === sitePreferences.timezone);
        } else if (defaultTimezone) {
            const canonicalTimezone = getTimezoneSynonym(defaultTimezone);

            selectableTimezone = timezoneOptions.find((stz) => stz.key === canonicalTimezone);

            // Sometimes implementing pages won't have a default timezone right away. If we get into a situation
            // where we don't have a timezone set in site preferences, but we suddenly do have a default timezone
            // then we need to force update the selected timezone (in the below useEffect). This will overwrite the
            // fallback selected timezone, as defined in the return statement below.
            setUpdateSelectedTimezone(true);
        }

        return selectableTimezone || timezoneOptions[2];
    }, [defaultTimezone, sitePreferences.timezone, timezoneOptions]);

    useEffect(() => {
        if (selectedTimezone === undefined || updateSelectedTimezone) {
            onChangeTimezone(defaultSelectableTimezone);
            setUpdateSelectedTimezone(false);
        }
    }, [defaultSelectableTimezone, onChangeTimezone, selectedTimezone, updateSelectedTimezone]);

    const ariaLabels = useMemo(() => {
        const labels = {
            dropdownButton: intl.formatMessage({
                defaultMessage: 'Expand Timezone Options',
                description:
                    'Label for select element button, prompting user to expand timezone options in a dropdown menu',
            }),
            optionsBox: intl.formatMessage({
                defaultMessage: 'Timezone Options',
                description: 'Label indicating that user is viewing a list of timezone options for a select component',
            }),
            selectLabel: intl.formatMessage({
                defaultMessage: 'Time Zone',
                description: 'A label for an input which allows the user to select the time zone to view time in.',
            }),
        };
        return labels;
    }, [intl]);

    return (
        <Select
            aria-label={ariaLabels.selectLabel}
            dropdownButtonAriaLabel={ariaLabels.dropdownButton}
            onOptionChange={onOptionChanged}
            options={timezoneOptions}
            optionsBoxAriaLabel={ariaLabels.optionsBox}
            selectedOption={selectedTimezone || null}
        />
    );
};

export default TimezonePicker;
